import * as React from "react";

import {
  GenericFormConsumerInterface,
  GenericReactProviderFormInterfaceOutput,
  useForm,
} from "../../hooks";
import { useLoginApp } from "../login-app-provider";

import { useLoginForm_asController } from '../login-form-provider';

import {
  PRESENTATION_SCROLL_ACTIONS,
  usePresenterForScroll_asController,
} from "../presenter-for-scroll-provider";
import { useProminentError } from "../prominent-error-provider";

import {
  initialJoinForm,
  JoinForm,
  JoinFormApp,
  JoinFormLogic,
} from "./join-form";

interface JoinFormConsumerInterface
  extends GenericFormConsumerInterface<JoinForm> {
  join: () => void;

  isLoading: boolean;
}
const defaultValue = {} as unknown as JoinFormConsumerInterface;
const JoinFormContext = React.createContext(defaultValue);

interface JoinForm_ControllerConsumerInterface {
  resetJoinForm: () => void;
}
const defaultControllerValue =
  {} as unknown as JoinForm_ControllerConsumerInterface;
const JoinFormControllerContext = React.createContext(defaultControllerValue);

interface JoinFormProviderProps {
  useInitialState: () => { initialState: JoinFormApp["initialState"] };
}
export const JoinFormProvider: React.FunctionComponent<
  JoinFormProviderProps
> = (props) => {
  const { shouldLoginUsingTheSpecialAsynchronousApiInsteadOfFormPost } =
    useLoginApp();

  const { initialState } = props.useInitialState();
  const { setProminentError } = useProminentError();
  const { initiateScrollAction } = usePresenterForScroll_asController();

  const { loginWithJoinForm } = useLoginForm_asController();

  const [isLoading, setIsLoading] = React.useState(false);

  const appContext = React.useMemo(
    () => ({
      initialState,
    }),
    [initialState]
  );
  const AppContext = React.useCallback(() => appContext, [appContext]);
  const thisForm = useForm(JoinFormLogic, AppContext, initialJoinForm);
  const { form: joinForm, validateRightNowAndGiveMeAYesOrNo } = thisForm;

  const joinApi = React.useCallback(async () => {
    try {
      setIsLoading(true);

      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Store", initialState.store_code);

      if (initialState.join_form_recaptcha.recaptchaEnabled) {
        const captchaWrapperId =
          initialState.join_form_recaptcha.recaptchaId + "-react-wrapper";
        let recaptchaInput = document.querySelector(
          "form.bbnt-common-join-form-ui > .input-container.recaptcha-input.named-recaptcha .g-recaptcha-response"
        ) as HTMLTextAreaElement;
        recaptchaInput =
          recaptchaInput ||
          document
            .querySelector("#" + captchaWrapperId)
            ?.querySelector(".g-recaptcha-response");
        if (recaptchaInput) {
          headers.append("X-ReCaptcha", recaptchaInput.value);
        }
      }

      const query = `
        mutation {
            createCustomer(
                input: {
                    firstname: "${joinForm.firstName}"
                    lastname: "${joinForm.lastName}"
                    email: "${joinForm.emailAddress}"
                    password: "${joinForm.password}"
                    is_subscribed: ${joinForm.subscribe}
                    mobile: "${joinForm.mobile || ""}"
                }
            ) {
                customer {
                    email
                    is_subscribed
                }
            }
        }
      `;

      const request = new Request("/graphql", {
        method: "POST",
        headers,
        body: JSON.stringify({ query }),
      });

      const joinResponse = await fetch(request);

      const joinResponseJson = await joinResponse?.json();

      if (joinResponseJson?.errors) {
        let errorMessage =
          "There was a problem creating your account. Please try again.";

        if (Array.isArray(joinResponseJson?.errors)) {
          const firstError = joinResponseJson.errors[0];
          if (firstError) {
            errorMessage = firstError.message;
          }
        }

        throw new Error(errorMessage);
      }

      /**
       * Need to wait a little bit because Magento creating a new customer
       * needs just a slight wait before it will complete fully
       */
      await new Promise((resolve) => setTimeout(resolve, 1000));

      (window as any).dataLayer = Array.isArray((window as any).dataLayer)
        ? (window as any).dataLayer
        : [];

      (window as any).dataLayer.push({
        event: "sign_up",
        method: "UsernamePassword",
        joinTime: new Date().toString(),
        firstname: joinForm.firstName,
        email: joinForm.emailAddress,
      });

      (window as any).dataLayer.push({
        event: "join",
        metadata: {
          method: "Google",
          joinTime: new Date().toString(),
          firstname: joinForm.firstName,
          email: joinForm.emailAddress,
        },
      });

      await loginWithJoinForm(joinForm);

      if (shouldLoginUsingTheSpecialAsynchronousApiInsteadOfFormPost) {
        setIsLoading(false);
      } else {
        /**
         * No need to set the loading off ->
         * page will be in the middle of redirecting if all goes well,
         * if anything has gone wrong, it will be handled by the catch block
         */
        // setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);

      let errorMessage = "An unknown error has occurred.";
      if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }

      setProminentError(errorMessage);
    }
  }, [
    shouldLoginUsingTheSpecialAsynchronousApiInsteadOfFormPost,
    setIsLoading,
    initialState,
    joinForm,
    loginWithJoinForm,
    setProminentError,
  ]);

  const join = React.useCallback(() => {
    const isValid = validateRightNowAndGiveMeAYesOrNo();
    if (isValid) {
      joinApi();
    } else {
      setProminentError(
        "Some information is missing or invalid. Check the red fields above for details."
      );
      initiateScrollAction(
        PRESENTATION_SCROLL_ACTIONS.SCROLL_TO_FORM_ERROR_FIELD
      );
    }
  }, [
    validateRightNowAndGiveMeAYesOrNo,
    joinApi,
    setProminentError,
    initiateScrollAction,
  ]);

  const value: JoinFormConsumerInterface = React.useMemo(
    () => ({
      ...GenericReactProviderFormInterfaceOutput<JoinForm, JoinFormApp>(
        thisForm
      ),

      join,
      isLoading,
    }),
    [thisForm, join, isLoading]
  );

  const controllerValue: JoinForm_ControllerConsumerInterface = React.useMemo(
    () => ({
      resetJoinForm: thisForm.resetForm,
    }),
    [thisForm]
  );

  return (
    <JoinFormContext.Provider value={value}>
      <JoinFormControllerContext.Provider value={controllerValue}>
        {props?.children || []}
      </JoinFormControllerContext.Provider>
    </JoinFormContext.Provider>
  );
};

export const useJoinForm = () => {
  const context = React.useContext(JoinFormContext);
  if (context === defaultValue) {
    throw new Error("useJoinForm must be used within a JoinFormProvider");
  }
  return context;
};

export const useJoinForm_asController = () => {
  const context = React.useContext(JoinFormControllerContext);
  if (context === defaultControllerValue) {
    throw new Error(
      "useJoinForm_asController must be used within a JoinFormProvider"
    );
  }
  return context;
};
